/**
 * MFALoginGate — Full-screen MFA verification after login.
 *
 * Used when user has enforcement_level = 'every_login'.
 * Wraps the app and blocks rendering until MFA is verified.
 * Cancel signs the user out.
 */

import React from 'react';
import { useAuth } from '../../context/AuthContext';
import MFAVerify from './MFAVerify';
import './MFA.css';

const MFALoginGate = ({ children }) => {
  const {
    currentUser,
    mfaStatus,
    mfaVerifiedThisSession,
    setMfaVerifiedThisSession,
    getAccessToken,
    logout,
  } = useAuth();

  // Only gate if user is logged in, MFA is enabled, and enforcement is every_login
  const needsLoginMFA = currentUser
    && mfaStatus?.mfaEnabled
    && mfaStatus?.enforcementLevel === 'every_login'
    && !mfaVerifiedThisSession;

  if (!needsLoginMFA) {
    return children;
  }

  const handleVerify = async ({ token, backupCode }) => {
    try {
      const accessToken = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || ''}/api/mfa/verify`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ token, backupCode }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || data.message || 'Verification failed' };
      }

      setMfaVerifiedThisSession(true);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const handleCancel = () => {
    logout();
  };

  return (
    <MFAVerify
      title="Login Verification"
      onVerify={handleVerify}
      onCancel={handleCancel}
    />
  );
};

export default MFALoginGate;
